import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { getPortal, type Portal } from './utils/subdomain';
import AppShell from './components/Layout/AppShell';

// Pages
import LoginPage from './pages/Login';
import POSPage from './pages/POS/POSPage';
import InventoryPage from './pages/Inventory/InventoryPage';
import InventoryForm from './pages/Inventory/InventoryForm';
import CustomersPage from './pages/Customers/CustomersPage';
import SessionPage from './pages/Session/OpenSession';
import CashflowPanel from './pages/Session/CashflowPanel';
import InvoicePage from './pages/Invoices/InvoicePage';
import InvoiceDetail from './pages/Invoices/InvoiceDetail';
import TablesPage from './pages/Tables/TablesPage';
import VaultPage from './pages/Vault/VaultPage';
import ManagerDashboard from './pages/Manager/ManagerDashboard';
import ReportsPage from './pages/Manager/ReportsPage';
import StoreSettings from './pages/Manager/StoreSettings';
import UserManagement from './pages/Manager/UserManagement';
import GeneralSettings from './pages/Manager/GeneralSettings';
import SupplierManagement from './pages/Manager/SupplierManagement';
import FilingCompliance from './pages/Manager/FilingCompliance';
import RentalPage from './pages/Modules/Rental/RentalPage';
import JobsPage from './pages/Modules/Jobs/JobsPage';
import PackagePage from './pages/Modules/Package/PackagePage';
import AppointmentPage from './pages/Modules/Appointment/AppointmentPage';
import TradeInPage from './pages/Modules/TradeIn/TradeInPage';
import ServicePage from './pages/Modules/Service/ServicePage';
import VoucherPage from './pages/Modules/Voucher/VoucherPage';
import ModuleShell from './pages/Modules/ModuleShell';

// Resolve portal once at module level
const portal: Portal = getPortal();

// --- Route Guards --------------------------------------------------------

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// --- Default landing per portal ------------------------------------------

function portalIndex(p: Portal): string {
  switch (p) {
    case 'manager':
      return '/dashboard';
    case 'vault':
      return '/vault';
    case 'distributor':
      return '/inventory';
    default:
      return '/pos';
  }
}

// --- Route definitions per portal ----------------------------------------

function retailRoutes() {
  return [
    <Route key="idx" index element={<Navigate to="/pos" replace />} />,
    <Route key="pos" path="pos" element={<POSPage />} />,
    <Route key="inv" path="inventory" element={<InventoryPage />} />,
    <Route key="inv-new" path="inventory/new" element={<InventoryForm />} />,
    <Route key="inv-id" path="inventory/:id" element={<InventoryForm />} />,
    <Route key="cust" path="customers" element={<CustomersPage />} />,
    <Route key="sess" path="session" element={<SessionPage />} />,
    <Route key="cf" path="session/cashflow" element={<CashflowPanel />} />,
    <Route key="tbl" path="tables" element={<TablesPage />} />,
    <Route key="invoices" path="invoices" element={<InvoicePage />} />,
    <Route key="inv-detail" path="invoices/:id" element={<InvoiceDetail />} />,
    <Route key="vault" path="vault" element={<VaultPage />} />,
    <Route key="rental" path="modules/rental" element={<ModuleShell><RentalPage /></ModuleShell>} />,
    <Route key="jobs" path="modules/jobs" element={<ModuleShell><JobsPage /></ModuleShell>} />,
    <Route key="package" path="modules/package" element={<ModuleShell><PackagePage /></ModuleShell>} />,
    <Route key="appointment" path="modules/appointment" element={<ModuleShell><AppointmentPage /></ModuleShell>} />,
    <Route key="tradein" path="modules/tradein" element={<ModuleShell><TradeInPage /></ModuleShell>} />,
    <Route key="service" path="modules/service" element={<ModuleShell><ServicePage /></ModuleShell>} />,
    <Route key="voucher" path="modules/voucher" element={<ModuleShell><VoucherPage /></ModuleShell>} />,
  ];
}

function managerRoutes() {
  return [
    <Route key="idx" index element={<Navigate to="/dashboard" replace />} />,
    <Route key="dash" path="dashboard" element={<ManagerDashboard />} />,
    <Route key="reports" path="reports" element={<ReportsPage />} />,
    <Route key="settings" path="settings" element={<StoreSettings />} />,
    <Route key="users" path="users" element={<UserManagement />} />,
    <Route key="general" path="general" element={<GeneralSettings />} />,
    <Route key="suppliers" path="suppliers" element={<SupplierManagement />} />,
    <Route key="filing" path="filing" element={<FilingCompliance />} />,
    <Route key="inv" path="inventory" element={<InventoryPage />} />,
    <Route key="inv-new" path="inventory/new" element={<InventoryForm />} />,
    <Route key="inv-id" path="inventory/:id" element={<InventoryForm />} />,
    <Route key="cust" path="customers" element={<CustomersPage />} />,
    <Route key="invoices" path="invoices" element={<InvoicePage />} />,
    <Route key="inv-detail" path="invoices/:id" element={<InvoiceDetail />} />,
  ];
}

function vaultRoutes() {
  return [
    <Route key="idx" index element={<Navigate to="/vault" replace />} />,
    <Route key="vault" path="vault" element={<VaultPage />} />,
    <Route key="invoices" path="invoices" element={<InvoicePage />} />,
    <Route key="inv-detail" path="invoices/:id" element={<InvoiceDetail />} />,
  ];
}

function distributorRoutes() {
  return [
    <Route key="idx" index element={<Navigate to="/inventory" replace />} />,
    <Route key="inv" path="inventory" element={<InventoryPage />} />,
    <Route key="inv-new" path="inventory/new" element={<InventoryForm />} />,
    <Route key="inv-id" path="inventory/:id" element={<InventoryForm />} />,
    <Route key="suppliers" path="suppliers" element={<SupplierManagement />} />,
    <Route key="cust" path="customers" element={<CustomersPage />} />,
    <Route key="invoices" path="invoices" element={<InvoicePage />} />,
    <Route key="inv-detail" path="invoices/:id" element={<InvoiceDetail />} />,
  ];
}

function getPortalRoutes(p: Portal) {
  switch (p) {
    case 'manager':
      return managerRoutes();
    case 'vault':
      return vaultRoutes();
    case 'distributor':
      return distributorRoutes();
    default:
      return retailRoutes();
  }
}

// --- Guard wrapper per portal --------------------------------------------

function PortalGuard({ children }: { children: React.ReactNode }) {
  if (portal === 'manager' || portal === 'distributor') {
    return <AdminRoute>{children}</AdminRoute>;
  }
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

// --- App -----------------------------------------------------------------

export { portal };

export default function App() {
  const landing = portalIndex(portal);

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected: wrapped in AppShell */}
      <Route
        path="/"
        element={
          <PortalGuard>
            <AppShell />
          </PortalGuard>
        }
      >
        {getPortalRoutes(portal)}
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
